// Third-party Imports
import Cookies from 'js-cookie'

// Type Imports
import type { Settings } from '@core/contexts/settingsContext'
import type { Mode, SystemMode } from '@core/types'

// Config Imports
import themeConfig from '@configs/themeConfig'

export const getSettingsFromCookie = (): Settings => {
  const cookieName = themeConfig.settingsCookieName
  const cookieValue = Cookies.get(cookieName)

  try {
    return cookieValue ? JSON.parse(cookieValue) : {}
  } catch {
    return {}
  }
}

export const getMode = async (): Promise<Mode> => {
  const settingsCookie = getSettingsFromCookie()

  // Get mode from cookie or fallback to theme config
  const _mode = settingsCookie.mode || themeConfig.mode

  return _mode
}

export const getSystemMode = async (): Promise<SystemMode> => {
  const mode = await getMode()

  // Get system preference from localStorage or default to light
  const colorPref = (localStorage.getItem('colorPref') as SystemMode) || 'light'

  return (mode === 'system' ? colorPref : mode) || 'light'
}

export const getServerMode = async (): Promise<string> => {
  const mode = await getMode()
  const systemMode = await getSystemMode()

  return mode === 'system' ? systemMode : mode
}

export const getSkin = async (): Promise<string> => {
  const settingsCookie = getSettingsFromCookie()

  return settingsCookie.skin || 'default'
}

// Helper to save settings to cookie
export const saveSettingsToCookie = (settings: Partial<Settings>) => {
  const cookieName = themeConfig.settingsCookieName
  const currentSettings = getSettingsFromCookie()
  const newSettings = { ...currentSettings, ...settings }

  Cookies.set(cookieName, JSON.stringify(newSettings), { expires: 365 })
}

// Helper to save system preference
export const saveSystemPreference = (preference: SystemMode) => {
  localStorage.setItem('colorPref', preference)
}
